import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import { showConfirmDialog } from '../components/ConfirmDialog.js';
import { formatCrowns } from '../util/formatBig.js';
import { cgTable, cgCol, cgRow } from '../util/costReward.js';
import type {
  DomicileTierDto,
  DomicileUpgradeDto,
  DomicileFurnishingDto,
  OwnedDomicileDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

// ──────────────────────────────────────────────────────────────────────────────
// Section grouping — mirrors the per-category accordion pattern used on the
// Misdeeds and Sparring pages. The slug → category maps are written by hand
// because the catalog doesn't carry a "class" or "category" field today; if a
// new tier or upgrade is added without an entry it falls into the trailing
// "other" group so it still renders.
// ──────────────────────────────────────────────────────────────────────────────

const TIER_CATEGORY_ORDER = ['common', 'gentry', 'noble', 'crown', 'other'] as const;
type TierCategory = (typeof TIER_CATEGORY_ORDER)[number];

const TIER_CATEGORY_LABELS: Record<TierCategory, string> = {
  common: 'Common Roof',
  gentry: 'Gentry’s Hold',
  noble: 'Noble Stronghold',
  crown: 'Crown of the Realm',
  other: 'Other',
};

const TIER_CATEGORY: Record<string, TierCategory> = {
  hovel: 'common',
  cottage: 'common',
  townhouse: 'common',
  manor: 'gentry',
  estate: 'gentry',
  keep: 'noble',
  castle: 'noble',
  citadel: 'noble',
  palace: 'crown',
  bastion: 'crown',
  sovereigns_hold: 'crown',
};

const UPGRADE_CATEGORY_ORDER = ['practical', 'grand', 'treasures', 'other'] as const;
type UpgradeCategory = (typeof UPGRADE_CATEGORY_ORDER)[number];

const UPGRADE_CATEGORY_LABELS: Record<UpgradeCategory, string> = {
  practical: 'Practical Comforts',
  grand: 'Grand Apartments',
  treasures: 'Treasures of State',
  other: 'Other',
};

const UPGRADE_CATEGORY: Record<string, UpgradeCategory> = {
  strongroom: 'practical',
  chapel: 'practical',
  royal_stable: 'practical',
  library: 'practical',
  bath_house: 'practical',
  cellar: 'practical',
  garrison: 'practical',
  solar: 'grand',
  menagerie: 'grand',
  observatory: 'grand',
  pleasure_garden: 'grand',
  great_hall: 'treasures',
  reliquary: 'treasures',
  war_room: 'treasures',
};

function tierCategoryOf(slug: string): TierCategory {
  return TIER_CATEGORY[slug] ?? 'other';
}
function upgradeCategoryOf(slug: string): UpgradeCategory {
  return UPGRADE_CATEGORY[slug] ?? 'other';
}

export async function renderDomiciles(root: HTMLElement): Promise<void> {
  let tiers: DomicileTierDto[] = [];
  let upgrades: DomicileUpgradeDto[] = [];
  let furnishings: DomicileFurnishingDto[] = [];
  let owned: OwnedDomicileDto | null = null;
  let flash: FlashState | null = null;

  // Preserve which accordions the user had open across rebuilds. `null` means
  // "first render — pick a sensible default" (the category that contains the
  // current tier for tiers, and the first installable category for upgrades).
  let openTiers: Set<TierCategory> | null = null;
  let openUpgrades: Set<UpgradeCategory> | null = null;

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      const cat = await api.listDomiciles();
      tiers = cat.tiers;
      upgrades = cat.upgrades;
      furnishings = cat.furnishings;
      owned = (await api.myDomicile()).domicile;
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  mountShell(root, (main) => {
    const renderMain = async () => {
      // Snapshot which accordions the user has open right now so the rebuild
      // doesn't slam everything shut.
      const prevTierCats = main.querySelectorAll<HTMLDetailsElement>('details.dom-tier-section[open]');
      if (prevTierCats.length > 0) {
        openTiers = new Set<TierCategory>();
        prevTierCats.forEach((d) => {
          const c = d.dataset.cat as TierCategory | undefined;
          if (c) openTiers!.add(c);
        });
      }
      const prevUpgradeCats = main.querySelectorAll<HTMLDetailsElement>('details.dom-upgrade-section[open]');
      if (prevUpgradeCats.length > 0) {
        openUpgrades = new Set<UpgradeCategory>();
        prevUpgradeCats.forEach((d) => {
          const c = d.dataset.cat as UpgradeCategory | undefined;
          if (c) openUpgrades!.add(c);
        });
      }

      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/domiciles.webp" alt="" />
        <h1>Domiciles</h1>
        <p style="font-style: italic; color: var(--c-ink-soft); margin-top: -8px;">
          A roof, a hearth, a place the world cannot reach.
        </p>

        <div id="flash"></div>

        <div id="owned-panel"></div>

        <div id="upkeep-panel"></div>

        <div id="vault-panel"></div>

        <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Property Tiers</h2>
        <p style="font-style: italic; color: var(--c-ink-soft);">Once bought, a residence is yours forever — there are no refunds and no selling. Moving to a different tier (grander or more modest) costs full price, and every upgrade you've installed stays behind with the old home. Any crowns in your vault return to your wallet on the move, so nothing is lost.</p>
        <div id="tier-cats"></div>

        <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Upgrades</h2>
        <p style="font-style: italic; color: var(--c-ink-soft);">Each takes one slot. Tier-determined slot count. Once installed, an upgrade lives in that residence forever.</p>
        <div id="upgrade-cats"></div>

        <h2 style="font-family:var(--font-display); color:var(--c-oxblood); border-bottom:1px solid var(--c-rule); padding-bottom:var(--s-1); margin: var(--s-5) 0 var(--s-3) 0;">Furnishings</h2>
        <p style="font-style: italic; color: var(--c-ink-soft);">Place a crafted ware for a lift to your composure and a small comfort besides. Furnishings have their own slots (half your upgrade slots, rounded up) and no upkeep — removing one returns the ware to your pack.</p>
        <div id="furnishing-grid" class="tier-grid"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .tier-grid {
          display: grid;
          /* min(360px, 100%) so a narrow phone collapses the column to fit the
             screen instead of forcing a 360px track that overflows — that
             blowout is what pushed the full-bleed card art past the edge. */
          grid-template-columns: repeat(auto-fit, minmax(min(360px, 100%), 1fr));
          gap: var(--s-3);
          padding: 0 var(--s-3) var(--s-3);
        }
        .tier-card, .upg-card, .owned-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex; flex-direction: column;
          /* Clip the full-bleed artwork (negative-margin width trick) to the
             card's rounded frame so it can't spill past the right edge. */
          overflow: hidden;
        }
        .tier-card.current {
          border-color: var(--c-gold);
          box-shadow: 0 0 0 2px rgba(180, 139, 28, 0.15);
        }
        /* Per-domicile / per-upgrade artwork strip. Hidden until the matching
           asset exists (onerror removes it) so missing files show nothing. */
        .tier-card .domicile-art, .upg-card .upgrade-art {
          display: block;
          width: calc(100% + 2 * var(--s-3));
          margin: calc(-1 * var(--s-3)) calc(-1 * var(--s-3)) var(--s-2);
          height: 180px;
          object-fit: cover;
          border-bottom: 1px solid var(--c-rule);
          border-radius: var(--radius) var(--radius) 0 0;
        }
        .tier-card h3, .upg-card h3 { margin: 0 0 4px 0; font-size: 1.05rem; }
        .tier-card .blurb, .upg-card .blurb {
          font-style: italic; color: var(--c-ink-soft); font-size: 0.92rem;
          margin-bottom: var(--s-2);
        }
        /* Table hugs the blurb text; the buy button is pushed to the card's
           bottom edge so buttons line up across the row. */
        .tier-card .cg, .upg-card .cg { margin-bottom: auto; }
        .pill {
          display: inline-block;
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 1px 8px;
          font-family: var(--font-numeric);
          color: var(--c-ink-soft);
          font-size: 0.85rem;
          margin-right: var(--s-1);
          margin-bottom: 4px;
        }
        /* (gold pill styling lives in pages.css — don't override here) */
        .pill.spirit { border-color: var(--c-spirit); color: var(--c-spirit); }
        .pill.success { border-color: var(--c-success); color: var(--c-success); }
        .pill.installed { border-color: var(--c-success); color: var(--c-success); font-weight: bold; }

        /* Collapsible category accordions — mirrors the Misdeeds .crime-section
           pattern so the whole game feels consistent. One <details> per
           category, the open caret sits where the disclosure triangle would. */
        .dom-tier-section, .dom-upgrade-section {
          margin-bottom: var(--s-3);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          background: var(--c-parchment);
        }
        .dom-tier-section summary, .dom-upgrade-section summary {
          cursor: pointer;
          padding: var(--s-2) var(--s-3);
          font-family: var(--font-display);
          color: var(--c-oxblood);
          font-size: 1rem;
          letter-spacing: 0.02em;
          list-style: none;
          user-select: none;
          display: flex;
          align-items: center;
          gap: var(--s-2);
        }
        .dom-tier-section summary::-webkit-details-marker,
        .dom-upgrade-section summary::-webkit-details-marker { display: none; }
        .dom-tier-section summary::before,
        .dom-upgrade-section summary::before {
          content: '\\25B8';
          color: var(--c-rule);
          display: inline-block;
          width: 1ch;
        }
        .dom-tier-section[open] summary::before,
        .dom-upgrade-section[open] summary::before { content: '\\25BE'; }
        .dom-tier-section summary:hover,
        .dom-upgrade-section summary:hover { background: rgba(139, 111, 71, 0.10); }
        .dom-section-count {
          margin-left: auto;
          font-family: var(--font-numeric);
          font-size: 0.85rem;
          color: var(--c-ink-soft);
        }
        .dom-section-here {
          font-family: var(--font-display);
          font-size: 0.72rem;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          background: var(--c-gold);
          color: #1c1108;
          padding: 1px 6px;
          border-radius: 3px;
        }

        .owned-panel {
          background: var(--c-parchment-dark);
          border: 2px solid var(--c-gold);
          border-radius: var(--radius);
          padding: 0;
          margin-bottom: var(--s-4);
          overflow: hidden;
          box-shadow: 0 4px 14px rgba(28, 17, 8, 0.18);
        }
        .owned-hero-wrap {
          position: relative;
          width: 100%;
          aspect-ratio: 7 / 3;
          max-height: 520px;
          overflow: hidden;
          border-bottom: 2px solid var(--c-gold);
          background: var(--c-parchment);
        }
        .owned-hero-art {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .owned-hero-name {
          position: absolute;
          left: 0; right: 0; bottom: 0;
          padding: var(--s-3) var(--s-4);
          background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.78) 100%);
          color: #fff5e1;
          font-family: var(--font-display);
          font-size: 1.75rem;
          letter-spacing: 0.05em;
          text-shadow: 0 2px 6px rgba(0,0,0,0.8);
          margin: 0;
        }
        .owned-body {
          padding: var(--s-3) var(--s-4) var(--s-4);
        }
      `;
      main.appendChild(style);

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      // Owned-domicile panel.
      const op = main.querySelector<HTMLDivElement>('#owned-panel')!;
      if (owned) {
        op.innerHTML = `
          <div class="owned-panel">
            <div class="owned-hero-wrap">
              <img class="owned-hero-art" src="/img/domiciles/${owned.tier}.webp?v=2" alt=""
                onerror="this.style.display='none'" />
              <h2 class="owned-hero-name">${escapeHtml(owned.tierName)}</h2>
            </div>
            <div class="owned-body">
              <p style="margin-top:0; color: var(--c-ink-soft); font-style: italic;">
                Acquired ${new Date(owned.acquiredAt).toLocaleDateString()} — yours forever.
              </p>
              <div>
                <span class="pill spirit">+${owned.spiritBonus} max spirit</span>
                <span class="pill">${owned.slotsUsed} / ${owned.upgradeSlots} slots used</span>
              </div>
              ${
                owned.upgrades.length === 0
                  ? '<p style="font-style:italic; color: var(--c-ink-soft); margin-top: var(--s-3); margin-bottom:0;">No upgrades installed.</p>'
                  : `<div style="margin-top: var(--s-3); display:flex; gap: var(--s-1); flex-wrap: wrap;">${owned.upgrades
                      .map(
                        (u) =>
                          `<span class="pill installed" style="display:inline-flex; align-items:center; gap: 6px;">${escapeHtml(u.name)} <button class="dom-uninstall-x" data-uninstall="${escapeHtml(u.slug)}" title="Uninstall ${escapeHtml(u.name)} (50% refund)" style="background:transparent; border:none; color: var(--c-oxblood); font-weight:bold; cursor:pointer; padding: 0 2px; line-height:1;">×</button></span>`,
                      )
                      .join('')}</div>`
              }
            </div>
          </div>
        `;
      } else {
        op.innerHTML = `
          <div style="font-style:italic; color:var(--c-ink-soft); padding: var(--s-3);">
            You own no domicile yet. Pick a tier below — even a Hovel is a roof of your own.
          </div>
        `;
      }

      // Upkeep panel — weekly running cost of the domicile + upgrades, plus
      // any accumulated debt. Only rendered when the player has a domicile.
      const up = main.querySelector<HTMLDivElement>('#upkeep-panel')!;
      if (owned) {
        try {
          const u = await api.getDomicileUpkeep();
          const perWeek = BigInt(u.perWeek);
          const debt = BigInt(u.currentDebt);
          const evictionAt = BigInt(u.evictionThreshold);
          if (perWeek > 0n || debt > 0n) {
            const debtPct = evictionAt > 0n ? Number((debt * 100n) / evictionAt) : 0;
            const debtTone = debt === 0n ? 'var(--c-ink-soft)' : debtPct >= 60 ? '#a8311e' : 'var(--c-oxblood)';
            up.innerHTML = `
              <div class="owned-panel" style="margin-top: var(--s-3);">
                <div class="owned-body">
                  <div style="display:flex; align-items:baseline; justify-content:space-between; gap: var(--s-3); flex-wrap: wrap;">
                    <h3 style="margin:0; font-family:var(--font-display); color:var(--c-oxblood);">Upkeep</h3>
                    <span class="pill">${formatCrowns(perWeek)} crowns / week</span>
                  </div>
                  <p style="margin: var(--s-2) 0 var(--s-3) 0; color: var(--c-ink-soft); font-style: italic;">
                    The household runs whether you're paying attention or not — wages, repairs, candle wax, hay for the stable. We charge once a week from your wallet. If your purse is empty the shortfall lands on a tab; if that tab grows past your home's full purchase price you'll be evicted.
                  </p>
                  <div style="display:grid; grid-template-columns: 1fr 1fr; gap: var(--s-3);">
                    <div>
                      <div style="color: var(--c-ink-soft); font-size: 0.9em;">Outstanding debt</div>
                      <div style="font-family:var(--font-display); font-size: 1.4em; color: ${debtTone};">${formatCrowns(debt)} crowns</div>
                      <div style="color: var(--c-ink-soft); font-size: 0.8em;">${debtPct}% of eviction threshold (${formatCrowns(evictionAt)})</div>
                    </div>
                    <div>
                      <div style="color: var(--c-ink-soft); font-size: 0.9em;">Next charge</div>
                      <div style="font-family:var(--font-display); font-size: 1.4em;">${u.weeksPending} week${u.weeksPending === 1 ? '' : 's'} pending</div>
                      <div style="color: var(--c-ink-soft); font-size: 0.8em;">~${formatCrowns(BigInt(u.amountPending))} when it ticks</div>
                    </div>
                  </div>
                  ${debt > 0n ? `
                    <div style="display:flex; gap: var(--s-2); flex-wrap: wrap; align-items:flex-end; margin-top: var(--s-3);">
                      <label style="flex:1; min-width: 180px;">
                        <span style="display:block; color: var(--c-ink-soft); font-size: 0.85em;">Pay down debt (wallet → debt)</span>
                        <input id="upkeep-pay-amount" type="number" min="1" step="1" placeholder="amount"
                          style="width: 100%; padding: var(--s-1) var(--s-2); border: 1px solid var(--c-rule); border-radius: 4px; background: var(--c-parchment);" />
                      </label>
                      <button id="upkeep-pay-btn" class="btn">Pay</button>
                      <button id="upkeep-pay-all" class="btn" style="background: var(--c-parchment); border: 1px solid var(--c-rule); color: var(--c-ink);">Pay All</button>
                    </div>
                  ` : ''}
                </div>
              </div>
            `;

            const payInp = up.querySelector<HTMLInputElement>('#upkeep-pay-amount');
            const payBtn = up.querySelector<HTMLButtonElement>('#upkeep-pay-btn');
            const payAll = up.querySelector<HTMLButtonElement>('#upkeep-pay-all');
            payAll?.addEventListener('click', () => {
              if (payInp) payInp.value = debt.toString();
            });
            payBtn?.addEventListener('click', async () => {
              const amt = payInp?.value?.trim() ?? '';
              if (!amt || amt === '0') return;
              try {
                const r = await api.payDomicileDebt(amt);
                store.set({ character: r.character });
                flash = { kind: 'success', message: `Paid ${formatCrowns(BigInt(r.paid))} crowns. ${BigInt(r.remainingDebt) === 0n ? 'Debt cleared.' : `${formatCrowns(BigInt(r.remainingDebt))} crowns remain.`}` };
                await refresh();
                anchored(renderMain);
              } catch (err) {
                flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Payment failed.' };
                anchored(renderMain);
              }
            });
          } else {
            up.innerHTML = '';
          }
        } catch {
          up.innerHTML = '';
        }
      } else {
        up.innerHTML = '';
      }

      // Personal vault panel — only shows when the player has Strongroom or
      // Personal Vault installed. Pulled lazily so the page doesn't pay the
      // /api/vault round-trip for players who can't use it yet.
      const vp = main.querySelector<HTMLDivElement>('#vault-panel')!;
      try {
        const v = await api.getVault();
        if (v.unlocked) {
          const vault = BigInt(v.vaultCrowns);
          const wallet = BigInt(v.walletCrowns);
          const upgradeName = 'Strongroom';
          vp.innerHTML = `
            <div class="owned-panel" style="margin-top: var(--s-3);">
              <div class="owned-body">
                <div style="display:flex; align-items:baseline; justify-content:space-between; gap: var(--s-3); flex-wrap: wrap;">
                  <h3 style="margin:0; font-family:var(--font-display); color:var(--c-oxblood);">Personal Vault</h3>
                  <span class="pill">${escapeHtml(upgradeName)} installed</span>
                </div>
                <p style="margin: var(--s-2) 0 var(--s-3) 0; color: var(--c-ink-soft); font-style: italic;">
                  Crowns kept here pay no interest — but no raider, plunder, or bank cap can touch them.
                </p>
                <div style="display:grid; grid-template-columns: 1fr 1fr; gap: var(--s-3); margin-bottom: var(--s-3);">
                  <div>
                    <div style="color: var(--c-ink-soft); font-size: 0.9em;">In vault</div>
                    <div style="font-family:var(--font-display); font-size: 1.4em;">${formatCrowns(vault)} crowns</div>
                  </div>
                  <div>
                    <div style="color: var(--c-ink-soft); font-size: 0.9em;">On hand (wallet)</div>
                    <div style="font-family:var(--font-display); font-size: 1.4em;">${formatCrowns(wallet)} crowns</div>
                  </div>
                </div>
                <div style="display:flex; gap: var(--s-2); flex-wrap: wrap; align-items:flex-end;">
                  <label style="flex:1; min-width: 180px;">
                    <span style="display:block; color: var(--c-ink-soft); font-size: 0.85em;">Deposit (wallet → vault)</span>
                    <input id="vault-deposit-amount" type="number" min="1" step="1" placeholder="amount"
                      style="width: 100%; padding: var(--s-1) var(--s-2); border: 1px solid var(--c-rule); border-radius: 4px; background: var(--c-parchment);" />
                  </label>
                  <button id="vault-deposit-btn" class="btn">Deposit</button>
                  <button id="vault-deposit-max" class="btn" style="background: var(--c-parchment); border: 1px solid var(--c-rule); color: var(--c-ink);">All</button>
                </div>
                <div style="display:flex; gap: var(--s-2); flex-wrap: wrap; align-items:flex-end; margin-top: var(--s-2);">
                  <label style="flex:1; min-width: 180px;">
                    <span style="display:block; color: var(--c-ink-soft); font-size: 0.85em;">Withdraw (vault → wallet)</span>
                    <input id="vault-withdraw-amount" type="number" min="1" step="1" placeholder="amount"
                      style="width: 100%; padding: var(--s-1) var(--s-2); border: 1px solid var(--c-rule); border-radius: 4px; background: var(--c-parchment);" />
                  </label>
                  <button id="vault-withdraw-btn" class="btn">Withdraw</button>
                  <button id="vault-withdraw-max" class="btn" style="background: var(--c-parchment); border: 1px solid var(--c-rule); color: var(--c-ink);">All</button>
                </div>
              </div>
            </div>
          `;

          const depBtn = vp.querySelector<HTMLButtonElement>('#vault-deposit-btn');
          const wdBtn = vp.querySelector<HTMLButtonElement>('#vault-withdraw-btn');
          const depMax = vp.querySelector<HTMLButtonElement>('#vault-deposit-max');
          const wdMax = vp.querySelector<HTMLButtonElement>('#vault-withdraw-max');
          const depInp = vp.querySelector<HTMLInputElement>('#vault-deposit-amount');
          const wdInp = vp.querySelector<HTMLInputElement>('#vault-withdraw-amount');

          depMax?.addEventListener('click', () => {
            if (depInp) depInp.value = wallet.toString();
          });
          wdMax?.addEventListener('click', () => {
            if (wdInp) wdInp.value = vault.toString();
          });

          depBtn?.addEventListener('click', async () => {
            const amt = depInp?.value?.trim() ?? '';
            if (!amt || amt === '0') return;
            try {
              const res = await api.vaultDeposit(amt);
              flash = { kind: 'success', message: `Deposited ${formatCrowns(BigInt(res.moved))} crowns into your vault.` };
              store.set({ character: res.character });
              await refresh();
              anchored(renderMain);
            } catch (err) {
              flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Deposit failed.' };
              anchored(renderMain);
            }
          });
          wdBtn?.addEventListener('click', async () => {
            const amt = wdInp?.value?.trim() ?? '';
            if (!amt || amt === '0') return;
            try {
              const res = await api.vaultWithdraw(amt);
              flash = { kind: 'success', message: `Withdrew ${formatCrowns(BigInt(res.moved))} crowns from your vault.` };
              store.set({ character: res.character });
              await refresh();
              anchored(renderMain);
            } catch (err) {
              flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Withdrawal failed.' };
              anchored(renderMain);
            }
          });
        } else if (BigInt(v.vaultCrowns) > 0n) {
          // Edge case: player had storage, vault has crowns, but they uninstalled
          // the upgrade. Allow withdrawal so funds aren't stranded.
          const vault = BigInt(v.vaultCrowns);
          vp.innerHTML = `
            <div class="owned-panel" style="margin-top: var(--s-3);">
              <div class="owned-body">
                <h3 style="margin:0; font-family:var(--font-display); color:var(--c-oxblood);">Vault (locked)</h3>
                <p style="margin-top: var(--s-2); color: var(--c-ink-soft); font-style: italic;">
                  You once had storage and ${formatCrowns(vault)} crowns remain inside. Install a Strongroom or Personal Vault to deposit, or withdraw what's left.
                </p>
                <div style="display:flex; gap: var(--s-2); align-items:flex-end; margin-top: var(--s-2);">
                  <button id="vault-withdraw-all" class="btn">Withdraw All (${formatCrowns(vault)})</button>
                </div>
              </div>
            </div>
          `;
          vp.querySelector<HTMLButtonElement>('#vault-withdraw-all')?.addEventListener('click', async () => {
            try {
              const res = await api.vaultWithdraw(vault.toString());
              flash = { kind: 'success', message: `Withdrew ${formatCrowns(BigInt(res.moved))} crowns from your vault.` };
              store.set({ character: res.character });
              await refresh();
              anchored(renderMain);
            } catch (err) {
              flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Withdrawal failed.' };
              anchored(renderMain);
            }
          });
        } else {
          vp.innerHTML = '';
        }
      } catch {
        // Best-effort UI — if /api/vault is unreachable, hide the panel.
        vp.innerHTML = '';
      }

      // ────────────────────────────────────────────────────────────────────
      // Tier accordions
      // ────────────────────────────────────────────────────────────────────
      const charCrowns = BigInt(char.crowns);
      const ownedTier = owned ? tiers.find((tt) => tt.slug === owned!.tier) : null;
      const ownedSpiritBonus = ownedTier?.spiritBonus ?? -1;
      const ownedCat: TierCategory | null = owned ? tierCategoryOf(owned.tier) : null;

      const tierCatsRoot = main.querySelector<HTMLDivElement>('#tier-cats')!;
      for (const cat of TIER_CATEGORY_ORDER) {
        const inCat = tiers.filter((t) => tierCategoryOf(t.slug) === cat);
        if (inCat.length === 0) continue;

        const section = document.createElement('details');
        section.className = 'dom-tier-section';
        section.dataset.cat = cat;
        // First render: open the category that holds the player's current tier
        // (so the next-step "Move up to..." card is right there). If they own
        // nothing yet, open the cheapest band so the Hovel is on screen.
        if (openTiers === null) {
          if (ownedCat === cat) section.open = true;
          else if (!ownedCat && cat === 'common') section.open = true;
        } else if (openTiers.has(cat)) {
          section.open = true;
        }

        const summary = document.createElement('summary');
        const hereBadge = ownedCat === cat
          ? '<span class="dom-section-here">You are here</span>'
          : '';
        summary.innerHTML = `<span>${escapeHtml(TIER_CATEGORY_LABELS[cat])}</span>${hereBadge}<span class="dom-section-count">${inCat.length}</span>`;
        section.appendChild(summary);

        const grid = document.createElement('div');
        grid.className = 'tier-grid';
        for (const t of inCat) {
          const isCurrent = owned?.tier === t.slug;
          const isDowngrade = !!owned && !isCurrent && t.spiritBonus < ownedSpiritBonus;
          const cost = BigInt(t.costCrowns);
          const canAfford = charCrowns >= cost;
          // Downgrades are allowed — players can move to smaller homes to
          // escape upkeep or repurpose wealth. Only block the current tier
          // and the can't-afford case.
          const disabled = isCurrent || !canAfford;
          let label: string;
          if (isCurrent) label = 'Currently owned';
          else if (!canAfford) label = 'Not enough crowns';
          else if (isDowngrade) label = `Move down to ${t.name}`;
          else label = owned ? `Move up to ${t.name}` : 'Buy';
          const card = document.createElement('div');
          card.className = `tier-card${isCurrent ? ' current' : ''}`;
          card.innerHTML = `
            <img class="domicile-art" src="/img/domiciles/${t.slug}.webp?v=2" alt=""
              onerror="this.remove()" />
            <h3>${escapeHtml(t.name)}</h3>
            <p class="blurb">${escapeHtml(t.blurb)}</p>
            ${cgTable([
              cgCol('Cost', [cgRow('Crowns', formatCrowns(t.costCrowns), canAfford ? '' : 'warn')]),
              cgCol('Reward', [
                cgRow('Max spirit', `+${t.spiritBonus}`, 'gain'),
                cgRow('Upgrade slots', String(t.upgradeSlots)),
              ]),
            ])}
            <button class="btn" data-buy-tier="${t.slug}" style="margin-top: var(--s-2);"
              ${disabled ? 'disabled' : ''}>
              ${escapeHtml(label)}
            </button>
          `;
          grid.appendChild(card);
        }
        section.appendChild(grid);
        tierCatsRoot.appendChild(section);
      }

      tierCatsRoot.querySelectorAll<HTMLButtonElement>('button[data-buy-tier]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.buyTier!;
          const target = tiers.find((tt) => tt.slug === slug);
          if (!target) return;

          // Confirm with a tier-appropriate warning. If they already own a
          // domicile, this is a tier-move that leaves upgrades behind.
          const isMove = !!owned;
          const message = isMove
            ? `Moving to ${target.name} costs ${formatCrowns(target.costCrowns)} crowns.\n\nYour current home and all installed upgrades stay behind — the new residence starts fresh, and you'll need to buy upgrades again. Any crowns in your vault return to your wallet first, so nothing is lost.\n\nThere are no refunds on the move itself.`
            : `Buy ${target.name} for ${formatCrowns(target.costCrowns)} crowns?\n\nOnce bought, a residence is yours forever — there are no refunds and no selling.`;
          if (!(await showConfirmDialog({
            title: isMove ? `Move up to ${target.name}?` : `Buy ${target.name}?`,
            message,
            confirmLabel: isMove ? `Move up — leave old home behind` : `Buy ${target.name}`,
          }))) return;

          tierCatsRoot.querySelectorAll<HTMLButtonElement>('button[data-buy-tier]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.buyDomicile({ tier: slug });
            store.set({ character: r.character });
            flash = {
              kind: 'success',
              message: isMove
                ? `Moved up to ${r.domicile.tierName} for ${formatCrowns(r.costPaid)} crowns. Your previous upgrades stayed behind — install fresh ones below.`
                : `Settled into ${r.domicile.tierName} for ${formatCrowns(r.costPaid)} crowns. Welcome home.`,
            };
            await refresh();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Purchase failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // ────────────────────────────────────────────────────────────────────
      // Upgrade accordions
      // ────────────────────────────────────────────────────────────────────
      const upgradeCatsRoot = main.querySelector<HTMLDivElement>('#upgrade-cats')!;
      let firstUpgradeCatRendered: UpgradeCategory | null = null;
      for (const cat of UPGRADE_CATEGORY_ORDER) {
        const inCat = upgrades.filter((u) => upgradeCategoryOf(u.slug) === cat);
        if (inCat.length === 0) continue;
        if (!firstUpgradeCatRendered) firstUpgradeCatRendered = cat;

        const section = document.createElement('details');
        section.className = 'dom-upgrade-section';
        section.dataset.cat = cat;
        // First render: open the cheapest installable category so players see
        // a buyable upgrade immediately.
        if (openUpgrades === null) {
          if (cat === firstUpgradeCatRendered) section.open = true;
        } else if (openUpgrades.has(cat)) {
          section.open = true;
        }

        const summary = document.createElement('summary');
        summary.innerHTML = `<span>${escapeHtml(UPGRADE_CATEGORY_LABELS[cat])}</span><span class="dom-section-count">${inCat.length}</span>`;
        section.appendChild(summary);

        const grid = document.createElement('div');
        grid.className = 'tier-grid';
        for (const u of inCat) {
          const installed = owned?.upgrades.some((o) => o.slug === u.slug) ?? false;
          const cost = BigInt(u.costCrowns);
          const canAfford = charCrowns >= cost;
          const haveSlot = (owned?.slotsRemaining ?? 0) > 0;

          const effectsRows: string[] = [];
          if (u.effects.travelSpeedBonusPct)
            effectsRows.push(cgRow('Travel', `-${u.effects.travelSpeedBonusPct}%`, 'gain'));
          if (u.effects.wageBonusPct)
            effectsRows.push(cgRow('Wages', `+${u.effects.wageBonusPct}%`, 'gain'));
          if (u.effects.elixirCooldownReduceSeconds)
            effectsRows.push(cgRow('Elixir cooldown', `-${Math.round(u.effects.elixirCooldownReduceSeconds / 60)} min`, 'gain'));
          if (u.effects.infirmaryReducePct)
            effectsRows.push(cgRow('Infirmary time', `-${u.effects.infirmaryReducePct}%`, 'gain'));
          if (u.effects.crownStorage) effectsRows.push(cgRow('Crown storage', 'Yes', 'gain'));
          if (u.effects.garrisonDefense) effectsRows.push(cgRow('Garrison defense', 'Yes', 'gain'));

          const card = document.createElement('div');
          card.className = 'upg-card';
          card.innerHTML = `
            <img class="upgrade-art" src="/img/domicile-upgrades/${u.slug}.webp?v=2" alt=""
              onerror="this.remove()" />
            <h3>${escapeHtml(u.name)}</h3>
            <p class="blurb">${escapeHtml(u.blurb)}</p>
            ${cgTable([
              cgCol('Cost', [cgRow('Crowns', formatCrowns(u.costCrowns), canAfford ? '' : 'warn')]),
              cgCol('Reward', [cgRow('Max spirit', `+${u.spiritBonus}`, 'gain'), ...effectsRows]),
            ])}
            <button class="btn" data-install="${u.slug}" style="margin-top: var(--s-2);"
              ${installed || !owned || !haveSlot || !canAfford ? 'disabled' : ''}>
              ${installed ? 'Installed' : !owned ? 'Need a domicile first' : !haveSlot ? 'No slots' : !canAfford ? 'Not enough crowns' : 'Install'}
            </button>
          `;
          grid.appendChild(card);
        }
        section.appendChild(grid);
        upgradeCatsRoot.appendChild(section);
      }

      upgradeCatsRoot.querySelectorAll<HTMLButtonElement>('button[data-install]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.install!;
          upgradeCatsRoot.querySelectorAll<HTMLButtonElement>('button[data-install]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.installDomicileUpgrade({ upgradeSlug: slug });
            store.set({ character: r.character });
            flash = { kind: 'success', message: `Installed for ${formatCrowns(r.costPaid)} crowns.` };
            await refresh();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Install failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // Uninstall buttons on the installed-upgrade pills in the owned-domicile
      // panel. Confirm first because the refund is only 50% of the install cost.
      main.querySelectorAll<HTMLButtonElement>('button[data-uninstall]').forEach((btn) => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const slug = btn.dataset.uninstall!;
          const upgrade = upgrades.find((u) => u.slug === slug);
          const name = upgrade?.name ?? slug;
          const refund = upgrade ? Math.floor(upgrade.costCrowns * 0.5) : 0;
          if (!(await showConfirmDialog({
            title: `Uninstall ${name}?`,
            message: `Removing ${name} refunds ${formatCrowns(refund)} crowns (50% of install). Its effect — and its weekly upkeep — stops immediately. You can re-install it later at full price.`,
            confirmLabel: 'Uninstall',
            tone: 'danger',
          }))) return;
          try {
            const r = await api.uninstallDomicileUpgrade({ upgradeSlug: slug });
            store.set({ character: r.character });
            flash = { kind: 'success', message: `Uninstalled ${name} — refunded ${formatCrowns(r.refundReceived)} crowns.` };
            await refresh();
          } catch (err) {
            const msg = err instanceof HttpError ? err.message : 'Uninstall failed.';
            flash = { kind: 'danger', message: msg };
          } finally {
            anchored(renderMain);
          }
        });
      });

      // ────────────────────────────────────────────────────────────────────
      // Furnishings grid — place a crafted ware for spirit + a small perk.
      // ────────────────────────────────────────────────────────────────────
      const furnishGrid = main.querySelector<HTMLDivElement>('#furnishing-grid')!;
      furnishGrid.innerHTML = '';
      const fCounter = document.createElement('div');
      fCounter.style.cssText = 'grid-column: 1 / -1; margin-bottom: var(--s-1);';
      fCounter.innerHTML = owned
        ? `<span class="pill">${owned.furnishingSlotsUsed} / ${owned.furnishingSlots} furnishing slots used</span>`
        : '<p style="font-style:italic; color: var(--c-ink-soft); margin:0;">Buy a domicile to place furnishings.</p>';
      furnishGrid.appendChild(fCounter);
      for (const f of furnishings) {
        const placed = owned?.furnishings.some((o) => o.wareSlug === f.wareSlug) ?? false;
        const haveSlot = (owned?.furnishingSlotsRemaining ?? 0) > 0;
        const effectsRows: string[] = [];
        if (f.effects.wageBonusPct) effectsRows.push(cgRow('Wages', `+${f.effects.wageBonusPct}%`, 'gain'));
        if (f.effects.elixirCooldownReduceSeconds)
          effectsRows.push(cgRow('Elixir cooldown', `-${Math.round(f.effects.elixirCooldownReduceSeconds / 60)} min`, 'gain'));
        if (f.effects.infirmaryReducePct)
          effectsRows.push(cgRow('Infirmary time', `-${f.effects.infirmaryReducePct}%`, 'gain'));

        const card = document.createElement('div');
        card.className = 'upg-card';
        card.innerHTML = `
          <div style="display:flex; align-items:center; gap:var(--s-2); margin-bottom:var(--s-1);">
            <img src="/img/item-thumbs/${f.wareSlug}.webp?v=2" alt="" width="56" height="56" loading="lazy" onerror="this.style.display='none'" style="flex:none; border-radius:6px;" />
            <h3 style="margin:0;">${escapeHtml(f.name)}</h3>
          </div>
          <p class="blurb">${escapeHtml(f.blurb)}</p>
          ${cgTable([cgCol('Reward', [cgRow('Max spirit', `+${f.spiritBonus}`, 'gain'), ...effectsRows])])}
          ${placed
            ? `<button class="btn" data-remove-furnish="${f.wareSlug}" style="margin-top: var(--s-2);">Remove (returns the ware)</button>`
            : `<button class="btn" data-place-furnish="${f.wareSlug}" style="margin-top: var(--s-2);" ${!owned || !haveSlot ? 'disabled' : ''}>${!owned ? 'Need a domicile first' : !haveSlot ? 'No furnishing slots' : 'Place (uses one from your pack)'}</button>`}
        `;
        furnishGrid.appendChild(card);
      }

      furnishGrid.querySelectorAll<HTMLButtonElement>('button[data-place-furnish]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.placeFurnish!;
          furnishGrid.querySelectorAll<HTMLButtonElement>('button[data-place-furnish]').forEach((b) => (b.disabled = true));
          try {
            const r = await api.installDomicileFurnishing({ wareSlug: slug });
            store.set({ character: r.character });
            flash = { kind: 'success', message: 'Furnishing placed.' };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not place that furnishing.' };
          } finally {
            anchored(renderMain);
          }
        });
      });
      furnishGrid.querySelectorAll<HTMLButtonElement>('button[data-remove-furnish]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const slug = btn.dataset.removeFurnish!;
          btn.disabled = true;
          try {
            const r = await api.removeDomicileFurnishing({ wareSlug: slug });
            store.set({ character: r.character });
            flash = { kind: 'success', message: 'Furnishing removed — the ware is back in your pack.' };
            await refresh();
          } catch (err) {
            flash = { kind: 'danger', message: err instanceof HttpError ? err.message : 'Could not remove that furnishing.' };
          } finally {
            anchored(renderMain);
          }
        });
      });
    };

    anchored(renderMain);
  });
}

